import { fetcher } from '#lib/backendFetcher';

/**
 * Searches for companies by name via autocomplete.
 * @param {string} query - Search term (minimum 2 characters)
 * @returns {Promise<Array<Object>>}
 */
export const searchCompaniesByName = async (query) => {
	if (!query || query.trim().length < 2) return [];

	const result = await fetcher(`/api/v1/search/autocomplete/companies?q=${encodeURIComponent(query.trim())}`);

	return result.data ?? [];
};

/**
 * Selects a company from the autocomplete results and optionally creates a prospect query.
 * @param {string} globalCompanyId - UUID of the company
 * @returns {Promise<string>} globalCompanyId of the selected company (used for navigation)
 */
export const selectCompanyFromAutocomplete = async (globalCompanyId) => {
	const result = await fetcher('/api/v1/search/autocomplete/companies/select', {
		method: 'POST',
		body: { globalCompanyId },
	});

	return result.data.globalCompanyId;
};

/**
 * Fetches the full company details by the globalCompanyEntries ID.
 * @param {string} globalCompanyId - UUID of the company
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 */
export const fetchCompanyDetail = async (globalCompanyId) => {
	try {
		const data = await fetcher(`/api/v1/leads/companies/${globalCompanyId}`);
		return {
			data: {
				...data,
				globalCompanyId: data.id,
				rating: data.rating ?? null,
				reviewCount: data.reviewCount ?? null,
			},
			error: null,
		};
	} catch (error) {
		return { data: null, error };
	}
};

/**
 * Fetches the financial history of a company.
 * @param {string} globalCompanyId - UUID of the company
 * @returns {Promise<{data: Array<Object>, error: Error|null}>}
 */
export const fetchCompanyFinancialHistory = async (globalCompanyId) => {
	if (!globalCompanyId) return { data: [], error: null };

	try {
		const result = await fetcher(`/api/v1/leads/companies/${globalCompanyId}/financials`);
		return { data: result.financials ?? [], error: null };
	} catch (error) {
		return { data: [], error };
	}
};

/**
 * Fetches the events of a company.
 * @param {string} globalCompanyId - UUID of the company
 * @returns {Promise<{data: Array<Object>, error: Error|null}>}
 */
export const fetchCompanyEvents = async (globalCompanyId) => {
	if (!globalCompanyId) return { data: [], error: null };

	try {
		const result = await fetcher(`/api/v1/leads/companies/${globalCompanyId}/events`);
		return { data: result.events ?? [], error: null };
	} catch (error) {
		return { data: [], error };
	}
};

/**
 * Fetches the contact persons of a company.
 * @param {string} globalCompanyId - UUID of the company
 * @returns {Promise<{data: Array<Object>, error: Error|null}>}
 */
export const fetchCompanyContacts = async (globalCompanyId) => {
	if (!globalCompanyId) return { data: [], error: null };

	try {
		const result = await fetcher(`/api/v1/leads/companies/${globalCompanyId}/contacts`);
		return { data: result.contacts ?? [], error: null };
	} catch (error) {
		return { data: [], error };
	}
};

/**
 * Fetches the managing directors of a company.
 * @param {string} globalCompanyId - UUID of the company
 * @returns {Promise<{data: Array<Object>, error: Error|null}>}
 */
export const fetchCompanyDirectors = async (globalCompanyId) => {
	if (!globalCompanyId) return { data: [], error: null };

	try {
		const result = await fetcher(`/api/v1/leads/companies/${globalCompanyId}/directors`);
		return { data: result.directors ?? [], error: null };
	} catch (error) {
		return { data: [], error };
	}
};
